import React, { useEffect, useState } from "react";
import AdminAppBar from "../../adminComponents/AdminAppBar";
import Label from "../../Form/Label";
import Heading from "../../Form/Heading";
import { MdOutlineSettings } from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import GLPagesService from "../../Services/GLPagesService";
import SpinWheelService from "../../Services/SpinWheelService";
import { ToastContainer } from "react-toastify";
import { showErrorToast, showInfoToast, showSuccessToast } from "../../Constant/Notification";
import Select from "react-select";
import BackArrows from "../../Form/BackArrow/BackArrows";

const GLpagesCreateTemplate = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { GLPageId } = location.state || {};
  const [showPreview, setShowPreview] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkErrors , setLinkErrors] = useState([]);
  const [loadingState, setLoadingState] = useState({
    logo: false,
    banner: false,
    footerLogo: false,
  });

  const platformPatterns = {
    facebook: /^https?:\/\/(www\.)?facebook\.com\/.+$/,
    instagram: /^https?:\/\/(www\.)?instagram\.com\/.+$/,
    twitter: /^https?:\/\/(www\.)?twitter\.com\/.+$/,
    youtube: /^https?:\/\/(www\.)?youtube\.com\/.+$/,
    linkedin: /^https?:\/\/(www\.)?linkedin\.com\/.+$/,
  };

  

  const sectionToTypeMap = {
    topSellingProperties: "TopSelling",
    plotProperties: "Layout",
    villaProperties: "Villa",
    // resaleBuildingProperties: "Resale Building",
    // apartmentProperties: "Apartment",
    // resaleLandProperties: "Resale Land",
    // acresAndCentsProperties: "Acres and Cents",
  };

  const [sectionProperties, setSectionProperties] = useState({
    topSellingProperties: [],
    plotProperties: [],
    villaProperties: [],
    apartmentProperties: [],
    resaleBuildingProperties: [],
    resaleLandProperties: [],
    acresAndCentsProperties: [],
  });

  const initialFormData = {
    landingPageId: "",
    header: {
      logo: "",
      image: "",
      description: "",
    },
    topSellingProperties: {
      enable: false,
      propertyPages: [],
    },
    plotProperties: {
      enable: false,
      propertyPages: [],
    },
    villaProperties: {
      enable: false,
      propertyPages: [],
    },
    resaleBuildingProperties: {
      enable: false,
      propertyPages: [],
    },
    apartmentProperties: {
      enable: false,
      propertyPages: [],
    },
    resaleLandProperties: {
      enable: false,
      propertyPages: [],
    },
    acresAndCentsProperties: {
      enable: false,
      propertyPages: [],
    },
    footer: {
      logo: "",
      facebook: "",
      instagram: "",
      twitter: "",
      youtube: "",
      linkedin: "",
    },
    contactUs: {
      address: "",
      email: "",
      phoneNumber: "",
    },
  };

  const [formData, setFormData] = useState(initialFormData);
  const [selectedProperties, setSelectedProperties] = useState({
    topSellingProperties: [],
    plotProperties: [],
    villaProperties: [],
    apartmentProperties: [],
    resaleBuildingProperties: [],
    resaleLandProperties: [],
    acresAndCentsProperties: [],
  });

  useEffect(() => {
    const fetchGLPageData = async () => {
      try {
        setLoading(true);
        const response = await GLPagesService.getLandingGLpage();
        if (
          response.status === 200 &&
          response.data &&
          response.data.length > 0
        ) {
          const data = response.data[0];
          setFormData(data);

          const newSelectedProps = { ...selectedProperties };

          for (const section of Object.keys(sectionToTypeMap)) {
            if (
              data[section]?.enable &&
              data[section]?.propertyPages?.length > 0
            ) {
              await fetchPropertiesForSection(section);

              newSelectedProps[section] = data[section].propertyPages.map(
                (prop) => ({
                  value: prop.glPageId,
                  label: prop.name,
                  id: prop.id,
                  order: prop.order,
                })
              );
            }
          }

          setSelectedProperties(newSelectedProps);
        }
      } catch (error) {
        console.error("Error loading GL page:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGLPageData();
  }, []);

  const fetchPropertiesForSection = async (section) => {
    try {
      const type = sectionToTypeMap[section];
      const propertiesResponse = await GLPagesService.getGLpageProperyName(
        type
      );

      if (propertiesResponse?.data) {
        const options = propertiesResponse.data.map((p) => ({
          value: p.id,
          label: p.name,
          glPageId: p.id,
        }));

        setSectionProperties((prev) => ({
          ...prev,
          [section]: options,
        }));
      }
    } catch (error) {
      showErrorToast(`Error loading properties for ${section}`);
      console.error(`Error loading properties for ${section}:`, error);
    }
  };

  const handleSectionToggle = async (section, enabled) => {
    setFormData((prev) => {
      const prevSection = prev[section] ?? { enable: false, propertyPages: [] };

      return {
        ...prev,
        [section]: {
          ...prevSection,
          enable: enabled,
          propertyPages: enabled ? prevSection.propertyPages : [],
        },
      };
    });

    if (
      enabled &&
      (!sectionProperties[section] || sectionProperties[section].length === 0)
    ) {
      try {
        await fetchPropertiesForSection(section);
      } catch (error) {
        console.error(`Error fetching properties for ${section}:`, error);
      }
    }
  };

  const handleImageUpload = async (file, type) => {
    try {
      setLoadingState((prev) => ({ ...prev, [type]: true }));
      const formData = new FormData();
      formData.append("file", file);
      const response = await SpinWheelService.createBannerImageTemplate(
        formData,
        "Landing Gl Page"
      );

      if (response?.data?.imageUrl) {
        return response.data.imageUrl;
      }
      throw new Error("Failed to get image URL");
    } catch (error) {
      showErrorToast("Image upload failed");
      console.error("Image upload error:", error);
      return null;
    } finally {
      setLoadingState((prev) => ({ ...prev, [type]: false }));
    }
  };

  const handleImageChange = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;
  
    const allowedTypes = [
      "image/png",
      "image/svg+xml",
      "image/webp",
      "image/jpeg",
      "image/jpg",
    ];
  
    if (!allowedTypes.includes(file.type)) {
      showErrorToast("Only PNG, SVG, WebP, JPG or JPEG images are allowed.");
      return;
    }
  
    const maxSizeMB = 1;
    if (file.size > maxSizeMB * 1024 * 1024) {
      showErrorToast("Image must be less than 1MB.");
      return;
    }
  
    const validDimensions = {
      logo: { width: 100, height: 35 },
      banner: { width: 1300, height: 700 },
      footerLogo: { width: 100, height: 35 },
    };
  
    const expectedSize = validDimensions[type];
    if (!expectedSize) {
      showErrorToast("Invalid image type selected.");
      return;
    }
  
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);
    image.src = objectUrl;
  
    image.onload = async () => {
      const { width, height } = image;
      URL.revokeObjectURL(objectUrl);
  
      const tolerance = 10; 
  
      const isWidthInRange = width >= (expectedSize.width - tolerance) && width <= (expectedSize.width + tolerance);
      const isHeightInRange = height >= (expectedSize.height - tolerance) && height <= (expectedSize.height + tolerance);
  
      if (!isWidthInRange || !isHeightInRange) {
        showInfoToast(
          `Please upload an image close to ${expectedSize.width}x${expectedSize.height}px for "${type}". Allowed tolerance is ±${tolerance}px.`
        );
        e.target.value = null;
       
      }
  
      try {
        const imageUrl = await handleImageUpload(file, type);
        if (!imageUrl) return;
  
        setFormData((prev) => {
          const updates = { ...prev };
          switch (type) {
            case "logo":
              updates.header = { ...updates.header, logo: imageUrl };
              break;
            case "banner":
              updates.header = { ...updates.header, image: imageUrl };
              break;
            case "footerLogo":
              updates.footer = { ...updates.footer, logo: imageUrl };
              break;
            default:
              break;
          }
          return updates;
        });
      } catch (error) {
        console.error("Image upload failed:", error);
        showErrorToast("Failed to upload image. Please try again.");
      }
    };
  
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      showErrorToast("Invalid image file.");
    };
  };
 
  

  const handleDeleteImage = async (imageUrl, type) => {
    try {
      await GLPagesService.DeleteGLPageImage(imageUrl);
      setFormData((prev) => {
        const updates = { ...prev };
        switch (type) {
          case "logo":
            updates.header.logo = "";
            break;
          case "banner":
            updates.header.image = "";
            break;
          case "footerLogo":
            updates.footer.logo = "";
            break;
          default:
            break;
        }
        return updates;
      });
    } catch (error) {
      showErrorToast("Error deleting image");
      console.error("Error deleting image:", error);
    }
  };

  console.log("Form Data Landing Page ID:", formData.landingPageId);

  const handleSubmit = async () => {
    try {
      setLoading(true);


      if (!formData.header.logo || !formData.header.image) {
        showErrorToast("Header logo and image are required");
        setLoading(false);
        return;
      }

      if (!formData.header.description) {
        showErrorToast("Header description is required");
        setLoading(false);
        return;
      }
      
      if (!formData.footer.logo ) {
        showErrorToast("Footer logo is required");
        setLoading(false);
        return;
      }


      const submitData = { ...formData };

      console.log("submitData  : ", submitData);
      Object.keys(sectionToTypeMap).forEach((section) => {
        const sectionData = submitData[section];
        const selected = selectedProperties[section];
        console.log(`Processing section: ${section}`, selected);

        if (
          sectionData &&
          sectionData.enable &&
          selectedProperties[section]?.length > 0
        ) {
          sectionData.propertyPages = selectedProperties[section].map(
            (prop, index) => ({
              id: prop.id || undefined,
              glPageId: prop.value,
              name: prop.label,
              order: index + 1,
            })
          );
        } else if (sectionData) {
          sectionData.propertyPages = [];
        }
      });

      const response = formData.landingPageId
        ? await GLPagesService.updateLandingGLpage(submitData)
        : await GLPagesService.postLandGLPage(submitData);

      if (response) {
        showSuccessToast(
          `GL page ${
            formData.landingPageId ? "updated" : "created"
          } successfully`
        );
      }
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setLoading(false);
    }
  };

  const renderImageUpload = (type, label, currentImage) => (
    <div className="flex flex-col items-center w-[60%]">
      <label
        htmlFor={`${type}-file`}
        className="flex flex-col items-center justify-center w-full h-40 border-2 border-customGreen border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
      >
        <div className="flex flex-col items-center justify-center pt-5 pb-6">
          <svg
            className="w-8 h-8 mb-4 text-customGreen"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 20 16"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"
            />
          </svg>
          <p className="mb-2 text-sm text-gray-500">
            <span className="font-semibold">Click to upload</span> or drag and
            drop
          </p>
          <p className="text-xs text-gray-500">
          PNG, SVG, WebP, JPG or JPEG {" "}
            {type === "logo"
              ? "100 x 35 px"
              : type === "banner"
              ? "1300 x 700 px"
              : "100 x 35 px"}
          </p>
        </div>
        <input
          id={`${type}-file`}
          type="file"
          className="hidden"
          onChange={(e) => handleImageChange(e, type)}
          accept="image/*"
        />
      </label>

      {loadingState[type] && (
        <div className="mt-4 text-center">
          <div className="spinner" />
          <p className="text-sm text-gray-600">Uploading...</p>
        </div>
      )}

      {currentImage && !loadingState[type] && (
        <div className="mt-4 relative">
          <img
            src={currentImage}
            alt={label}
            className="w-16 h-16 rounded-lg shadow-md border border-gray-200"
          />
          <button
            type="button"
            className="absolute top-0 right-0 text-[9px] w-4 h-4 text-red-500 bg-white rounded-full  shadow-md"
            onClick={() => handleDeleteImage(currentImage, type)}
          >
            X
          </button>
        </div>
      )}
    </div>
  );

  const renderSectionToggle = (section) => (
    <label className="inline-flex cursor-pointer w-[60%] items-center">
      <input
        type="checkbox"
        checked={formData[section]?.enable}
        onChange={(e) => handleSectionToggle(section, e.target.checked)}
        className="sr-only peer"
      />
       <div className="relative w-9 h-5 bg-red-500 rounded-full peer-checked:bg-emerald-600 after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-full" />
        
    </label>
  );

  const handlePropertyChange = (section, selected) => {
    setSelectedProperties((prev) => ({
      ...prev,
      [section]: selected || [],
    }));
  };

  const renderPropertySelector = (section) => {
    const selected = selectedProperties[section] || [];
    const selectedValues = selected.map((item) => item.value);
    const allOptions = sectionProperties[section] || [];
  
    const availableOptions = allOptions.filter(
      (option) => !selectedValues.includes(option.value)
    );
  
    return (
      <Select
  options={sectionProperties[section].map((option) => ({
    ...option,
    isDisabled: selectedProperties[section].some(
      (selected) => selected.value === option.value
    ),
  }))}
  value={selectedProperties[section]}
  isMulti
  onChange={(selectedOptions) => {
    setSelectedProperties((prev) => ({
      ...prev,
      [section]: selectedOptions,
    }));
  }}
/>

    );
  };
  
  
  
  

  return (
    <div className="bg-appGrayBackground min-h-screen pt-20">
       <AdminAppBar />
      <div className="px-14 pb-10 mt-4">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <BackArrows onClick={() => navigate(-1)} />
            <h1 className="text-3xl font-bold">
              {formData.landingPageId ? "Edit" : "New"} GL Page
            </h1>
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="bg-emerald-600 text-white py-2 px-4 rounded disabled:bg-gray-400"
            >
              {loading
                ? "Saving..."
                : formData.landingPageId
                ? "Update"
                : "Create"}
            </button>
          </div>
        </div>

        {/* {showPreview && formData.landingPageId && (
          <iframe
            src={`http://vr-pages.glfunnel.com.s3-website-us-east-1.amazonaws.com/glpage/${formData.landingPageId}`}
            className="w-full h-[600px] border-0"
            title="Preview"
          />
        )} */}

        <div className="bg-white rounded-md p-6 mt-5">
          {/* Header Section */}
          <Heading text="Header" Icon={MdOutlineSettings} />
          <div className="px-10 pt-6 pb-16 space-y-6">
            <div className="flex justify-between">
            
           <Label label="Header Logo" isAsterisk={true} /> 
              {renderImageUpload(
                "logo",
                "Header Logo",
                formData.header?.logo || ""
              )}
              
            </div>

            <div className="flex justify-between">
              <Label label="Header Image" isAsterisk={true} />
              {renderImageUpload(
                "banner",
                "Header Image",
                formData.header?.image || ""
              )}
            </div>
            <div className="flex justify-between">
              <Label label="Description" isAsterisk={true} />
              <input
                value={formData.header?.description || ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    header: { ...prev.header, description: e.target.value },
                  }))
                }
                maxLength={120}
                placeholder="Enter Description"
                className="w-[60%] px-4 py-2 bg-slate-100 border rounded-md"
              />
            </div>
          </div>

          {/* Property Sections */}
          {Object.keys(sectionToTypeMap).map((section) => (
            <div key={section}>
              <Heading
                // text={section
                //   .replace(/([A-Z])/g, " $1")
                //   .replace(/^./, (str) => str.toUpperCase())
                //   .replace("Properties", "")}
                text={sectionToTypeMap[section]}

                Icon={MdOutlineSettings}
              />
              <div className="px-10 pt-6 pb-16 space-y-6">
                <div className="flex justify-between">
                  <Label label="Enable Section" />
                  {renderSectionToggle(section)}
                </div>
                <div className="flex justify-between">
                  <Label label="Properties" />
                  <div className="w-[60%]">
                    {renderPropertySelector(section)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Footer Section */}
          <Heading text="Footer" Icon={MdOutlineSettings} />
<div className="px-10 pt-6 pb-16 space-y-6">
  <div className="flex justify-between">
    <Label label="Footer Logo" isAsterisk={true} />
    {renderImageUpload("footerLogo", "Footer Logo", formData?.footer?.logo || "")}
  </div>
  {["facebook", "instagram", "twitter", "youtube", "linkedin"].map((platform) => (
  <div key={platform} className="flex justify-between">
    <div className="flex flex-col">
      <Label label={`${platform.charAt(0).toUpperCase() + platform.slice(1)} Link`} />
      
      <div className="text-red-500 text-sm">
        {linkErrors[platform] && (
          <div className="text-red-500 text-sm">
            Please enter a valid {platform} URL starting with http:// or https://
          </div>
        )}
      </div>
    </div>
    
    <div className="w-[60%]">
      <input
        value={formData?.footer?.[platform] || ""}
        onChange={(e) => {
          const value = e.target.value;
          const pattern = platformPatterns[platform]; // assuming this is predefined
          
          // Check if the URL matches the pattern and update state accordingly
          if (value === "" || pattern.test(value)) {
            setFormData((prev) => ({
              ...prev,
              footer: { ...prev.footer, [platform]: value },
            }));
            setLinkErrors((prev) => ({
              ...prev,
              [platform]: value && !pattern.test(value),
            }));
          } else {
            // If the URL doesn't match, don't update form data
            setLinkErrors((prev) => ({
              ...prev,
              [platform]: true,
            }));
          }
        }}
        placeholder={`Enter ${platform} link`}
        className="w-full px-4 py-2 bg-slate-100 border rounded-md"
        pattern="https?://(?:www\.)?[a-zA-Z0-9-]+\.[a-zA-Z]{2,6}(?:/[\w\-._~:/?#[\]@!$&'()*+,;=]*)?"
        title="Please enter a valid URL starting with http:// or https://"
        required
      />
    </div>
  </div>
))}

</div>




          {/* Contact Us Section */}
          <Heading text="Contact Us" Icon={MdOutlineSettings} />
          <div className="px-10 pt-6 pb-16 space-y-6">
            {["address", "email", "phoneNumber"].map((field) => (
              <div key={field} className="flex flex-col gap-1">
                <div className="flex justify-between items-center">
                  <Label
                    label={field.charAt(0).toUpperCase() + field.slice(1)}
                  />
                  <input
                    value={formData?.contactUs?.[field] || ""}
                    onChange={(e) => {
                      const value = e.target.value;

                      if (field === "address" && value.length > 120) return;

                      if (field === "email") {
                        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                        if (value && !emailRegex.test(value)) {
                          
                        }
                      }

                      if (field === "phoneNumber") {
                   
                        if (/[^0-9]/.test(value)) return;
          
                        if (value.length > 10) return;
                      }

                      setFormData((prev) => ({
                        ...prev,
                        contactUs: {
                          ...prev.contactUs,
                          [field]: value,
                        },
                      }));
                    }}
                    placeholder={`Enter ${
                      field.charAt(0).toUpperCase() + field.slice(1)
                    }`}
                    className="w-[60%] px-4 py-2 bg-slate-100 border rounded-md"
                  />
                </div>
                {/* Optional: Validation messages */}
                {field === "address" &&
                  formData?.contactUs?.[field]?.length > 120 && (
                    <p className="text-red-500 text-sm">
                      Address can't exceed 120 characters.
                    </p>
                  )}
                {field === "email" &&
                  formData?.contactUs?.[field] &&
                  !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                    formData?.contactUs?.[field]
                  ) && (
                    <p className="text-red-500 text-sm">
                      Invalid email format.
                    </p>
                  )}
                {field === "phoneNumber" &&
                  formData?.contactUs?.[field]?.length > 10 && (
                    <p className="text-red-500 text-sm">
                      Phone number must be 10 digits.
                    </p>
                  )}
              </div>
            ))}
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default GLpagesCreateTemplate;
